"use client";

import { useState } from "react";
import {
  BookingForm,
  type StayOption,
  type PaymentAvailability,
} from "./BookingForm";
import { SafeImage } from "@/components/SafeImage";
import { fallbackImage } from "@/data/properties";
import type { PricingSettings } from "@/lib/pricing";

export type BookOrgGroup = {
  id: string;
  name: string;
  logo: string | null;
  stays: StayOption[];
  checkInText: string;
  checkOutText: string;
};

// "Book now" opened without a preselected stay: pick the organization first
// (logo + name tabs), then the stay picker offers only that org's stays and the
// check-in/out info is the org's own. The form remounts per org (keyed), so
// dates/guests reset when switching — different host, different booking.
export function BookOrgTabs({
  groups,
  settings,
  payments,
  unavailable,
  prices,
}: {
  groups: BookOrgGroup[];
  settings: PricingSettings;
  payments: PaymentAvailability;
  unavailable: Record<string, string[]>;
  prices: Record<string, Record<string, number>>;
}) {
  const [activeId, setActiveId] = useState(groups[0]?.id);
  const active = groups.find((g) => g.id === activeId) ?? groups[0];
  if (!active) return null;

  return (
    <div>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-sand">
        {groups.map((g) => {
          const isActive = g.id === active.id;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setActiveId(g.id)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "border-forest-500 text-forest-700"
                  : "border-transparent text-stone hover:text-forest-600"
              }`}
            >
              {g.logo ? (
                <SafeImage
                  src={g.logo}
                  fallback={fallbackImage(g.id)}
                  alt=""
                  width={24}
                  height={24}
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <span className="grid h-6 w-6 place-items-center rounded-full bg-forest-100 text-[11px] font-bold text-forest-700">
                  {g.name.slice(0, 1).toUpperCase()}
                </span>
              )}
              {g.name}
            </button>
          );
        })}
      </div>

      {/* The active organization's own check-in / check-out info. */}
      <div className="mb-6 flex flex-wrap gap-x-6 gap-y-1 text-sm font-semibold text-forest-600">
        <span>{active.checkInText}</span>
        <span>{active.checkOutText}</span>
      </div>

      <BookingForm
        key={active.id}
        stays={active.stays}
        settings={settings}
        payments={payments}
        unavailable={unavailable}
        prices={prices}
      />
    </div>
  );
}
